import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { Icon } from '../components/Icons';
import { btnGhost, btnIcon, tableStyle, tdStyle, thStyle } from '../components/buttons';
import { creatorApi, type SubscriberQuery } from '../api/creator';
import { fmtDate, fmtUSD } from '../lib/format';

type Status = 'all' | 'active' | 'paused' | 'churned';
type Sort = 'joined' | 'months' | 'earned' | 'code' | 'status' | 'plan' | 'country';

export function SubscribersScreen() {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status>('all');
  const [codeFilter, setCodeFilter] = useState<string>('all');
  const [sort, setSort] = useState<{ key: Sort; dir: 'asc' | 'desc' }>({ key: 'joined', dir: 'desc' });
  const [page, setPage] = useState(1);

  const totals = useQuery({ queryKey: ['creator', 'totals'], queryFn: creatorApi.totals });
  const codes = useQuery({ queryKey: ['creator', 'codes'], queryFn: creatorApi.listCodes });
  const filterArgs: SubscriberQuery = {
    status: statusFilter,
    code: codeFilter,
    q: query || undefined,
    sort: sort.key,
    dir: sort.dir,
    page,
    pageSize: 50,
  };
  const subs = useQuery({
    queryKey: ['creator', 'subscribers', filterArgs],
    queryFn: () => creatorApi.subscribers(filterArgs),
  });

  const items = subs.data?.items ?? [];
  const t = totals.data;

  const setSortKey = (k: Sort) => {
    setSort((prev) => (prev.key === k ? { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'desc' }));
    setPage(1);
  };

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1280 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
        <KpiCard label="Active subscribers" value={t?.activeSubs ?? 0} sublabel="Renewing each cycle" accent />
        <KpiCard label="Total subscribers (all-time)" value={t?.totalCodeUses ?? 0} sublabel="Including churned" />
        <KpiCard
          label="Conversion rate"
          value={(t?.conversionRate ?? 0) * 100}
          format={(n) => n.toFixed(1) + '%'}
          decimals={1}
          sublabel="Code uses → paid subs"
        />
        <KpiCard
          label="Pending payout"
          value={t?.pendingPayoutCents ?? 0}
          format={fmtUSD}
          decimals={2}
          sublabel="Across all subscribers"
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '12px 14px',
          background: 'var(--brand-soft)',
          border: '1px solid color-mix(in oklab, var(--brand) 25%, transparent)',
          borderRadius: 'var(--r-md)',
          color: 'var(--brand-ink)',
          fontSize: 12.5,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M12 3 L20 7 V13 c0 4.5-3.5 7-8 8 c-4.5-1-8-3.5-8-8 V7 Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
        <div>
          <strong>Anonymized for privacy.</strong> You see a randomized Trainer ID, country, and plan — never names or emails. Earnings per sub stay attributed for the lifetime of their subscription.
        </div>
      </div>

      <Card padding={0}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--line)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--bg-sunken)',
              border: '1px solid var(--line)',
              padding: '6px 10px',
              borderRadius: 'var(--r-md)',
              color: 'var(--ink-3)',
              flex: '1 1 220px',
              minWidth: 200,
              maxWidth: 340,
            }}
          >
            <Icon.Search />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Search trainer ID or code…"
              style={{ border: 0, background: 'transparent', outline: 'none', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink)', flex: 1 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 'var(--r-pill)' }}>
            {(['all', 'active', 'paused', 'churned'] as const).map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--r-pill)',
                  fontSize: 12,
                  fontWeight: 500,
                  background: statusFilter === s ? 'var(--bg-elev)' : 'transparent',
                  color: statusFilter === s ? 'var(--ink)' : 'var(--ink-3)',
                  boxShadow: statusFilter === s ? 'var(--shadow-card)' : 'none',
                  textTransform: 'capitalize',
                }}
              >
                {s}
              </button>
            ))}
          </div>

          <select
            value={codeFilter}
            onChange={(e) => { setCodeFilter(e.target.value); setPage(1); }}
            style={{
              ...btnGhost,
              cursor: 'pointer',
              appearance: 'none',
              paddingRight: 28,
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%23807a6d' d='M0 0h10L5 6z'/></svg>\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
            }}
          >
            <option value="all">All codes</option>
            {(codes.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </select>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button style={btnGhost}>
              <Icon.Download />
              Export
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <SortTh k="code" sort={sort} onSort={setSortKey}>Trainer ID</SortTh>
                <SortTh k="country" sort={sort} onSort={setSortKey}>Region</SortTh>
                <SortTh k="plan" sort={sort} onSort={setSortKey}>Plan</SortTh>
                <SortTh k="code" sort={sort} onSort={setSortKey}>Code used</SortTh>
                <SortTh k="joined" sort={sort} onSort={setSortKey}>Joined</SortTh>
                <SortTh k="months" sort={sort} onSort={setSortKey} right>Months active</SortTh>
                <SortTh k="earned" sort={sort} onSort={setSortKey} right>Earned for you</SortTh>
                <SortTh k="status" sort={sort} onSort={setSortKey} right>Status</SortTh>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.trainerId} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-sunken)', border: '1px solid var(--line)', display: 'grid', placeItems: 'center', fontSize: 14 }}>
                        {s.country}
                      </div>
                      <span className="mono" style={{ fontWeight: 600 }}>Trainer {s.trainerId}</span>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 16 }}>{s.country}</td>
                  <td style={tdStyle}><Pill tone={s.plan === 'annual' ? 'brand' : 'neutral'}>{s.plan}</Pill></td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, background: 'var(--bg-sunken)', color: 'var(--ink-2)', padding: '2px 8px', borderRadius: 'var(--r-sm)' }}>
                      {s.code}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--ink-2)' }}>{fmtDate(s.joined)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }} className="num">{s.months}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }} className="num">{fmtUSD(s.earnedCents)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <Pill tone={s.status === 'active' ? 'good' : s.status === 'paused' ? 'warn' : 'neutral'}>{s.status}</Pill>
                  </td>
                </tr>
              ))}
              {items.length === 0 && !subs.isLoading && (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)', padding: '40px 0' }}>
                  No subscribers match those filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink-3)' }}>
          <span>
            Showing <span className="mono" style={{ color: 'var(--ink)' }}>{items.length}</span> of <span className="mono">{subs.data?.total ?? 0}</span> · live data updates daily
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button style={{ ...btnIcon, width: 30, height: 30 }} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>‹</button>
            <span style={{ display: 'inline-grid', placeItems: 'center', minWidth: 60, fontSize: 12, color: 'var(--ink-3)' }}>
              page {page} / {subs.data?.pages ?? 1}
            </span>
            <button style={{ ...btnIcon, width: 30, height: 30 }} onClick={() => setPage((p) => Math.min(subs.data?.pages ?? 1, p + 1))} disabled={page >= (subs.data?.pages ?? 1)}>›</button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function SortTh({
  k,
  sort,
  onSort,
  right,
  children,
}: {
  k: Sort;
  sort: { key: Sort; dir: 'asc' | 'desc' };
  onSort: (k: Sort) => void;
  right?: boolean;
  children: React.ReactNode;
}) {
  const active = sort.key === k;
  return (
    <th
      style={{ ...thStyle, textAlign: right ? 'right' : 'left', cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(k)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: active ? 'var(--ink)' : 'var(--ink-3)' }}>
        {children}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, opacity: active ? 1 : 0.3 }}>
          {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  );
}
